/**
 * Guide Component
 * Handles multi-step guide navigation with interactive device image
 */

if (!customElements.get('guide-container')) {
  customElements.define(
    'guide-container',
    class GuideContainer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id');
        this.steps = Array.from(this.querySelectorAll('.guide__step')).filter(
          step => !step.classList.contains('guide__step--result')
        );
        this.resultStep = this.querySelector('.guide__step--result');
        this.currentStepIndex = 0;
        this.answers = {};
        this.totalSteps = this.steps.length;

        this.nextButton = this.querySelector('[data-guide-next]');
        this.prevButton = this.querySelector('[data-guide-prev]');
        this.restartButton = this.querySelector('[data-guide-restart]');
        this.progressText = this.querySelector('[data-progress-text]');
        
        // Image elements
        this.imageContainer = this.querySelector('[data-guide-image-container]');
        this.indicators = {
          1: this.querySelector('[data-guide-indicator="1"]'),
          2: this.querySelector('[data-guide-indicator="2"]'),
          3: this.querySelector('[data-guide-indicator="3"]'),
          4: this.querySelector('[data-guide-indicator="4"]')
        };
        
        // SVG elements for dynamic color changes
        this.deviceSvg = this.querySelector('.guide__device-svg');
        this.initSvgElements();

        this.init();
      }
      
      initSvgElements() {
        if (!this.deviceSvg) return;
        
        // Get or create SVG elements that will change color based on light status
        // Power indicator (step 1) - circle element
        this.svgPowerLight = this.deviceSvg.querySelector('[data-svg-power-light]');
        if (!this.svgPowerLight) {
          // Find the circle element for power (around coordinates 39.46, 160.29)
          const circles = this.deviceSvg.querySelectorAll('circle, path');
          // We'll need to identify which element represents the power light
          // For now, we'll add data attributes to the SVG in the template
        }
        
        // Cellular signal indicator (step 2) - dots on the right
        this.svgCellularLight = this.deviceSvg.querySelector('[data-svg-cellular-light]');
        
        // Wi-Fi indicator (step 3) - signal bars
        this.svgWifiLight = this.deviceSvg.querySelector('[data-svg-wifi-light]');
        
        // Data transfer indicator (step 4) - arrow or other element
        this.svgDataLight = this.deviceSvg.querySelector('[data-svg-data-light]');
      }

      init() {
        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.handleNext.bind(this));
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', this.handlePrev.bind(this));
        }

        if (this.restartButton) {
          this.restartButton.addEventListener('click', this.handleRestart.bind(this));
        }

        // Listen for answer changes
        this.addEventListener('change', this.handleAnswerChange.bind(this));

        this.updateStepIndexAttribute();
        this.updateProgress();
        this.updateNavigation();
        this.updateImageState();
        this.updateSvgColors();
      }

      handleNext() {
        if (!this.validateCurrentStep()) {
          return;
        }

        this.saveCurrentStepAnswers();
        this.updateImageState();
        this.updateSvgColors();

        const currentStep = this.steps[this.currentStepIndex];
        const isLastStep = this.currentStepIndex === this.totalSteps - 1;

        // If we're on the last step, go to result step
        if (isLastStep && this.resultStep) {
          this.showResultStep();
        } else if (this.currentStepIndex < this.steps.length - 1) {
          // Regular step progression
          this.currentStepIndex++;
          this.updateStepIndexAttribute();
          this.showStep(this.currentStepIndex);
          this.updateProgress();
          this.updateNavigation();
          this.updateImageState();
          this.updateSvgColors();
        }
      }

      handlePrev() {
        if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.updateStepIndexAttribute();
          this.showStep(this.currentStepIndex);
          this.updateProgress();
          this.updateNavigation();
          this.updateImageState();
          this.updateSvgColors();
        }
      }

      handleRestart() {
        // Reset to first step
        this.currentStepIndex = 0;
        this.answers = {};
        this.showStep(0);
        this.updateProgress();
        this.updateNavigation();
        this.updateImageState();

        // Hide result step
        if (this.resultStep) {
          this.resultStep.classList.add('hidden');
        }

        // Reset all inputs
        this.steps.forEach(step => {
          const inputs = step.querySelectorAll('input');
          inputs.forEach(input => {
            if (input.type === 'radio' || input.type === 'checkbox') {
              input.checked = false;
            } else {
              input.value = '';
            }
          });
        });
      }

      showStep(index) {
        // Hide result step
        if (this.resultStep) {
          this.resultStep.classList.add('hidden');
        }

        this.steps.forEach((step, i) => {
          if (i === index) {
            step.classList.remove('hidden');
            step.setAttribute('aria-hidden', 'false');

            // Focus first input in step
            const firstInput = step.querySelector('input');
            if (firstInput && !firstInput.disabled) {
              setTimeout(() => firstInput.focus(), 100);
            }
          } else {
            step.classList.add('hidden');
            step.setAttribute('aria-hidden', 'true');
          }
        });
      }

      showResultStep() {
        // Save all answers before showing result
        this.steps.forEach((step, index) => {
          this.currentStepIndex = index;
          this.saveCurrentStepAnswers();
        });

        // Hide all regular steps
        this.steps.forEach(step => {
          step.classList.add('hidden');
        });

        // Show result step
        if (this.resultStep) {
          this.resultStep.classList.remove('hidden');
          this.updateProgress();
          this.updateNavigation();
          this.updateImageState();
          this.populateResults();
          
          // Scroll to result
          this.resultStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      populateResults() {
        const resultList = this.resultStep.querySelector('[data-guide-result-list]');
        if (!resultList) {
          return;
        }

        // Determine results based on user choices
        const results = this.determineResults();
        
        // Clear existing results
        resultList.innerHTML = '';

        // Create result items
        results.forEach((result, index) => {
          const resultItem = document.createElement('div');
          resultItem.className = 'guide__result-item';
          
          const ol = document.createElement('ol');
          ol.setAttribute('start', index + 1);
          
          const li = document.createElement('li');
          li.style.marginLeft = '22.5px';
          li.textContent = result;
          
          ol.appendChild(li);
          resultItem.appendChild(ol);
          resultList.appendChild(resultItem);
        });
      }

      determineResults() {
        // Get all answers sorted by step number
        const sortedAnswers = Object.values(this.answers)
          .sort((a, b) => a.stepNumber - b.stepNumber);

        const results = [];

        // Display answer for each step based on selected status
        sortedAnswers.forEach(answer => {
          if (answer.answerText && answer.answerText.trim() !== '') {
            results.push(answer.answerText.trim());
          }
        });

        // If no answers found, return empty array (let the UI handle empty state)
        return results;
      }

      validateCurrentStep() {
        const currentStep = this.steps[this.currentStepIndex];
        if (!currentStep) {
          return true;
        }

        // Check if at least one answer is selected for single choice
        const radioGroup = currentStep.querySelector('input[type="radio"]');
        if (radioGroup && !currentStep.querySelector('input[type="radio"]:checked')) {
          const radioInputs = currentStep.querySelectorAll('input[type="radio"]');
          radioInputs.forEach((input) => input.classList.add('invalid'));
          return false;
        }

        // Remove invalid class from all inputs
        const allInputs = currentStep.querySelectorAll('input');
        allInputs.forEach((input) => input.classList.remove('invalid'));

        return true;
      }

      getStateFromOptionValue(optionValue) {
        const selectedValue = String(optionValue || '').toLowerCase().trim();

        if (selectedValue.includes('red') && selectedValue.includes('solid')) {
          return 'red';
        }

        if (
          selectedValue.includes('white') &&
          selectedValue.includes('solid') &&
          !selectedValue.includes('blink')
        ) {
          return 'white';
        }

        if (selectedValue.includes('blink')) {
          return 'blink';
        }

        if (selectedValue.includes('off') || selectedValue.includes('light off')) {
          return 'off';
        }

        return 'off';
      }

      getStateFromRadioInput(radioInput) {
        if (!radioInput) {
          return 'off';
        }

        const rawState = radioInput.getAttribute('data-state');
        const normalizedState =
          typeof rawState === 'string' ? rawState.toLowerCase().trim() : '';

        const allowedStates = new Set(['red', 'white', 'blink', 'off']);
        if (allowedStates.has(normalizedState)) {
          return normalizedState;
        }

        // Backward-compatible fallback for older markup without data-state.
        return this.getStateFromOptionValue(radioInput.value);
      }

      saveCurrentStepAnswers() {
        const currentStep = this.steps[this.currentStepIndex];
        if (!currentStep) {
          return;
        }

        const stepId = currentStep.getAttribute('data-step-id');
        const stepNumber = parseInt(currentStep.getAttribute('data-step-number')) || this.currentStepIndex + 1;

        // Handle single choice (radio)
        const selectedRadio = currentStep.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          const optionIndex = parseInt(selectedRadio.getAttribute('data-option-index')) || 1;
          const state = this.getStateFromRadioInput(selectedRadio);

          // Get the answer text for this option from block settings
          const answerKey = `data-answer-option-${optionIndex}`;
          const answerText = currentStep.getAttribute(answerKey) || '';

          this.answers[stepId] = {
            value: selectedRadio.value,
            stepNumber: stepNumber,
            state: state,
            optionIndex: optionIndex,
            answerText: answerText
          };
          
          // Update data attributes on the container
          this.setAttribute(`data-step-${stepNumber}-value`, selectedRadio.value);
        }

      }
      
      updateStepIndexAttribute() {
        const currentStepNumber = this.currentStepIndex + 1;
        this.setAttribute('data-step-index', currentStepNumber.toString());
      }

      handleAnswerChange(event) {
        const input = event.target;
        if (input.hasAttribute('data-guide-answer')) {
          // Auto-save for immediate feedback
          const currentStep = this.steps[this.currentStepIndex];
          if (currentStep) {
            this.saveCurrentStepAnswers();
            this.updateImageState();
            this.updateSvgColors();
          }
        }
      }

      updateProgress() {
        if (!this.progressText) {
          return;
        }

        const currentStep = this.steps[this.currentStepIndex];
        const isResultStep = this.resultStep && !this.resultStep.classList.contains('hidden');
        const progressContainer = this.querySelector('[data-guide-progress]');

        // Hide progress text on result step
        if (progressContainer) {
          if (isResultStep) {
            progressContainer.setAttribute('data-guide-progress-hidden', '');
          } else {
            progressContainer.removeAttribute('data-guide-progress-hidden');
          }
        }

        let stepNumber;

        if (isResultStep) {
          stepNumber = this.totalSteps;
        } else if (currentStep) {
          stepNumber = this.currentStepIndex + 1;
        } else {
          stepNumber = 1;
        }

        if (this.progressText) {
          this.progressText.textContent = stepNumber;
        }
      }

      updateNavigation() {
        const currentStep = this.steps[this.currentStepIndex];
        const isFirstStep = this.currentStepIndex === 0;
        const isResultStep = this.resultStep && !this.resultStep.classList.contains('hidden');

        // Hide buttons on result step
        if (isResultStep) {
          if (this.prevButton) {
            this.prevButton.hidden = true;
          }
          if (this.nextButton) {
            this.nextButton.hidden = true;
          }
          return;
        }

        // Previous button - visible but with reduced opacity on first step
        if (this.prevButton) {
          if (isFirstStep) {
            this.prevButton.hidden = false;
            this.prevButton.classList.add('guide__button--disabled');
            this.prevButton.disabled = true;
          } else {
            this.prevButton.hidden = false;
            this.prevButton.classList.remove('guide__button--disabled');
            this.prevButton.disabled = false;
          }
        }

        // Next button
        if (this.nextButton) {
          this.nextButton.hidden = false;
        }
      }

      updateImageState() {
        const currentStepNumber = this.currentStepIndex + 1;
        const isResultStep = this.resultStep && !this.resultStep.classList.contains('hidden');

        // Remove all active classes first
        Object.keys(this.indicators).forEach(num => {
          const indicator = this.indicators[num];
          if (indicator) {
            indicator.classList.remove('guide__indicator--active');
          }
        });

        // If on result step, activate all indicators
        if (isResultStep) {
          Object.keys(this.indicators).forEach(num => {
            const indicator = this.indicators[num];
            if (indicator) {
              indicator.classList.add('guide__indicator--active');
            }
          });
          return;
        }

        // Only activate the current step's indicator
        const currentIndicator = this.indicators[currentStepNumber];
        if (currentIndicator) {
          currentIndicator.classList.add('guide__indicator--active');
        }
      }
      
      updateSvgColors() {
        if (!this.deviceSvg) return;
        
        // Update SVG colors based on answers for each step
        // Step 1: Power Indicator
        const step1Answer = Object.values(this.answers).find(answer => answer && answer.stepNumber === 1);
        if (step1Answer) {
          this.updateSvgElementColor('[data-svg-power-light]', step1Answer.state);
        }
        
        // Step 2: Cellular Signal Indicator
        const step2Answer = Object.values(this.answers).find(answer => answer && answer.stepNumber === 2);
        if (step2Answer) {
          this.updateSvgElementColor('[data-svg-cellular-light]', step2Answer.state);
        }
        
        // Step 3: Wi-Fi Indicator
        const step3Answer = Object.values(this.answers).find(answer => answer && answer.stepNumber === 3);
        if (step3Answer) {
          this.updateSvgElementColor('[data-svg-wifi-light]', step3Answer.state);
        }
        
        // Step 4: Data Transfer Indicator
        const step4Answer = Object.values(this.answers).find(answer => answer && answer.stepNumber === 4);
        if (step4Answer) {
          this.updateSvgElementColor('[data-svg-data-light]', step4Answer.state);
        }
      }
      
      updateSvgElementColor(selector, state) {
        const element = this.deviceSvg.querySelector(selector);
        if (!element) return;
        
        // Remove all state classes
        element.classList.remove('svg-light--red', 'svg-light--white', 'svg-light--blink', 'svg-light--off');
        
        // Apply state class for CSS-based color changes
        element.classList.add(`svg-light--${state}`);
        
        // For blink state, the CSS animation will handle the blinking effect
        // For other states, set inline styles as fallback
        switch(state) {
          case 'red':
            element.style.fill = '#FF0000';
            element.style.stroke = '#FF0000';
            break;
          case 'white':
            element.style.fill = '#FFFFFF';
            element.style.stroke = '#FFFFFF';
            break;
          case 'blink':
            // Set white color - CSS animation will handle the blinking
            element.style.fill = '#FFFFFF';
            element.style.stroke = '#FFFFFF';
            break;
          case 'off':
            element.style.fill = '#6E7884';
            element.style.stroke = '#6E7884';
            break;
        }
      }
    }
  );
}

